import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ShieldCheck, FileBadge, Timer, PoundSterling } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1614213951697-a45781262acf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxyb2FkJTIwY29uc3RydWN0aW9uJTIwd29ya2VyJTIwc2FmZXR5fGVufDB8fHx8MTc4NjE4NDUzOHww&ixlib=rb-4.1.0&q=85";

const STATS = [
    { icon: FileBadge, value: "100%", label: "Council paperwork handled by us" },
    { icon: Timer, value: "2 days", label: "Typical time on site" },
    { icon: ShieldCheck, value: "£5m", label: "Public liability insurance" },
    { icon: PoundSterling, value: "Fixed", label: "Price quoted before we start" },
];

export default function TrustSection() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <section id="trust" ref={ref} className="bg-concrete text-tarmac py-24 sm:py-32" data-testid="trust-section">
            <div className="px-5 sm:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                    <motion.p
                        className="font-mono text-xs tracking-[0.25em] uppercase mb-4"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                    >
                        [ Why Drop Kerbs UK ]
                    </motion.p>
                    <motion.h2
                        className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-8"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7 }}
                    >
                        No forms. No chasing.<br />No bounced bumpers.
                    </motion.h2>
                    <motion.p
                        className="text-base lg:text-lg text-tarmac/70 leading-relaxed max-w-lg mb-12"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        Driving over a full kerb wrecks your tyres, your suspension — and
                        it's illegal without an authorised crossing. We deal with the
                        council, the utilities and the tarmac so you don't have to.
                    </motion.p>

                    <div className="grid grid-cols-2 border-t border-l border-tarmac/20">
                        {STATS.map((s, i) => (
                            <motion.div
                                key={s.label}
                                data-testid={`trust-stat-${i}`}
                                className="border-b border-r border-tarmac/20 p-6 sm:p-8"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                            >
                                <s.icon size={22} className="mb-4 text-tarmac/60" />
                                <p className="font-display text-3xl sm:text-4xl mb-1">{s.value}</p>
                                <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-tarmac/60">{s.label}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <motion.div
                    className="relative overflow-hidden border border-tarmac/20 h-[420px] lg:h-[560px]"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8 }}
                >
                    <motion.img
                        src={IMG}
                        alt="Drop Kerbs UK crew member in safety gear on site"
                        className="w-full h-[116%] object-cover"
                        style={{ y }}
                    />
                    <div className="absolute bottom-0 left-0 bg-tarmac text-paper px-6 py-4">
                        <p className="font-mono text-xs tracking-[0.2em] uppercase text-amber">On site, on spec, on time</p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
